import { VALIDATION_LIMITS } from '@config/constants';
import { Check, FileText, Loader2 } from 'lucide-react';
import type { ChangeEvent, FormEvent, ReactElement, RefObject } from 'react';

type AuthMode = 'login' | 'register' | 'forgot' | 'reset-sent' | 'verification-sent';
type LegalDocType = 'terms' | 'privacy';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

interface AuthFormProps {
  mode: AuthMode;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName: string;
  inviteCode: string;
  inviteCodeFromUrl: string;
  hasInviteLink: boolean;
  rememberMe: boolean;
  tosAccepted: boolean;
  error: string;
  loading: boolean;
  inviteOnly: boolean;
  oauthGoogle: boolean;
  oauthApple: boolean;
  oauthLoading: 'google' | 'apple' | null;
  googleFallback: boolean;
  googleBtnRef: RefObject<HTMLDivElement | null>;
  viewedDocs: { terms: boolean; privacy: boolean };
  canAcceptTos: boolean;
  passwordStrength: PasswordStrength | null;
  showEmailError: boolean | string | null | undefined;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onInviteCodeChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
  onTosChange: (value: boolean) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onSwitchMode: (mode: AuthMode) => void;
  onGoogleSignIn: () => void;
  onAppleSignIn: () => void;
  onOpenLegalDoc: (type: LegalDocType) => void;
}

export function AuthForm({
  mode,
  email,
  password,
  firstName,
  lastName,
  displayName,
  inviteCode,
  inviteCodeFromUrl,
  hasInviteLink,
  rememberMe,
  tosAccepted,
  error,
  loading,
  inviteOnly,
  oauthGoogle,
  oauthApple,
  oauthLoading,
  googleFallback,
  googleBtnRef,
  viewedDocs,
  canAcceptTos,
  passwordStrength,
  showEmailError,
  onEmailChange,
  onPasswordChange,
  onFirstNameChange,
  onLastNameChange,
  onDisplayNameChange,
  onInviteCodeChange,
  onRememberMeChange,
  onTosChange,
  onSubmit,
  onSwitchMode,
  onGoogleSignIn,
  onAppleSignIn,
  onOpenLegalDoc,
}: AuthFormProps): ReactElement {
  return (
    <div
      data-testid="auth-form"
      className="bg-theme-surface rounded-lg p-6 border border-theme-border rounded-2xl shadow-xl shadow-black/20 backdrop-blur-sm"
    >
      {/* Tab Toggle - only show for login/register */}
      {mode !== 'forgot' && (
        <div className="flex mb-6">
          <button
            type="button"
            data-testid="auth-tab-login"
            onClick={() => onSwitchMode('login')}
            className={`flex-1 py-2 text-center font-medium transition ${
              mode === 'login'
                ? 'text-white border-b-2 border-theme-primary'
                : 'text-theme-text-secondary border-b border-theme-border hover:text-theme-text-secondary'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            data-testid="auth-tab-register"
            onClick={() => onSwitchMode('register')}
            className={`flex-1 py-2 text-center font-medium transition ${
              mode === 'register'
                ? 'text-white border-b-2 border-theme-primary'
                : 'text-theme-text-secondary border-b border-theme-border hover:text-theme-text-secondary'
            }`}
          >
            Sign Up
          </button>
        </div>
      )}

      {/* Forgot password header */}
      {mode === 'forgot' && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white mb-1">Reset Password</h3>
          <p className="text-sm text-theme-text-secondary">
            Enter your email to receive a reset link
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-theme-danger/50 border border-theme-danger rounded text-theme-danger text-sm">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        {mode === 'register' && (
          <>
            {inviteOnly && (
              <div className="p-3 bg-theme-primary/10 border border-theme-primary/30 rounded text-sm text-theme-text-secondary">
                Registration is invite-only. Enter the code from your invite email.
              </div>
            )}
            {inviteOnly && (
              <div>
                <label
                  htmlFor="inviteCode"
                  className="block text-sm text-theme-text-secondary mb-1"
                >
                  Invite Code <span className="text-theme-danger">*</span>
                </label>
                <input
                  id="inviteCode"
                  data-testid="auth-invite-code-input"
                  type="text"
                  value={inviteCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onInviteCodeChange(e.target.value)
                  }
                  required
                  readOnly={!!inviteCodeFromUrl}
                  className={`w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary ${
                    inviteCodeFromUrl ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  placeholder="Paste your invite code"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-sm text-theme-text-secondary mb-1">
                  First Name <span className="text-theme-danger">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onFirstNameChange(e.target.value)}
                  required
                  maxLength={VALIDATION_LIMITS.FIRST_NAME_MAX_LENGTH}
                  className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm text-theme-text-secondary mb-1">
                  Last Name <span className="text-theme-danger">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onLastNameChange(e.target.value)}
                  required
                  maxLength={VALIDATION_LIMITS.LAST_NAME_MAX_LENGTH}
                  className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary"
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label htmlFor="displayName" className="block text-sm text-theme-text-secondary mb-1">
                Display Name <span className="text-theme-danger">*</span>
              </label>
              <input
                id="displayName"
                data-testid="auth-display-name-input"
                type="text"
                value={displayName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onDisplayNameChange(e.target.value)}
                required
                maxLength={VALIDATION_LIMITS.DISPLAY_NAME_MAX_LENGTH}
                className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500"
                placeholder="How should we call you?"
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="email" className="block text-sm text-theme-text-secondary mb-1">
            Email
          </label>
          <input
            id="email"
            data-testid="auth-email-input"
            type="email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onEmailChange(e.target.value)}
            required
            readOnly={mode === 'register' && hasInviteLink}
            className={`w-full px-3 py-2 bg-theme-bg border rounded text-white placeholder-gray-500 focus:outline-none ${
              showEmailError
                ? 'border-theme-danger'
                : 'border-theme-border focus:border-theme-primary'
            } ${mode === 'register' && hasInviteLink ? 'opacity-70 cursor-not-allowed' : ''}`}
            placeholder="you@example.com"
          />
          {showEmailError && (
            <p className="mt-1 text-xs text-theme-danger">Please enter a valid email address</p>
          )}
        </div>

        {mode !== 'forgot' && (
          <div>
            <label htmlFor="password" className="block text-sm text-theme-text-secondary mb-1">
              Password
            </label>
            <input
              id="password"
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onPasswordChange(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary"
              placeholder="••••••••"
            />
            {/* Password strength indicator - only show on register */}
            {mode === 'register' && password && passwordStrength && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={`h-1 flex-1 rounded ${
                        level <= passwordStrength.score
                          ? passwordStrength.color
                          : 'bg-theme-surface-hover'
                      }`}
                    />
                  ))}
                </div>
                <p
                  className={`text-xs ${
                    passwordStrength.score <= 1
                      ? 'text-theme-danger'
                      : passwordStrength.score <= 2
                        ? 'text-theme-warning'
                        : passwordStrength.score <= 3
                          ? 'text-theme-primary'
                          : 'text-theme-success'
                  }`}
                >
                  {passwordStrength.label}
                  {passwordStrength.score < 3 && ' - Add uppercase, numbers, or symbols'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Remember me checkbox - only show on login */}
        {mode === 'login' && (
          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onRememberMeChange(e.target.checked)}
              className="w-4 h-4 bg-theme-bg border-theme-border rounded text-theme-primary focus:ring-theme-primary focus:ring-offset-theme-surface"
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-theme-text-secondary">
              Remember me
            </label>
          </div>
        )}

        {/* TOS acceptance checkbox - only show on register */}
        {mode === 'register' && (
          <div className="space-y-2">
            {/* Links to view documents */}
            <div className="flex flex-col gap-1 text-sm">
              <button
                type="button"
                onClick={() => onOpenLegalDoc('terms')}
                className={`flex items-center gap-2 text-left ${
                  viewedDocs.terms
                    ? 'text-theme-success'
                    : 'text-theme-primary hover:text-theme-primary-hover'
                }`}
              >
                {viewedDocs.terms ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span className={viewedDocs.terms ? '' : 'underline'}>
                  {viewedDocs.terms ? 'Terms of Service (read)' : 'Read Terms of Service'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onOpenLegalDoc('privacy')}
                className={`flex items-center gap-2 text-left ${
                  viewedDocs.privacy
                    ? 'text-theme-success'
                    : 'text-theme-primary hover:text-theme-primary-hover'
                }`}
              >
                {viewedDocs.privacy ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span className={viewedDocs.privacy ? '' : 'underline'}>
                  {viewedDocs.privacy ? 'Privacy Policy (read)' : 'Read Privacy Policy'}
                </span>
              </button>
            </div>

            {/* Agreement checkbox - only enabled after viewing both */}
            <div className={`flex items-start gap-2 ${!canAcceptTos ? 'opacity-50' : ''}`}>
              <input
                id="tosAccepted"
                data-testid="auth-tos-checkbox"
                type="checkbox"
                checked={tosAccepted}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onTosChange(e.target.checked)}
                disabled={!canAcceptTos}
                required
                className="w-4 h-4 mt-0.5 bg-theme-bg border-theme-border rounded text-theme-primary focus:ring-theme-primary focus:ring-offset-theme-surface disabled:cursor-not-allowed"
              />
              <label htmlFor="tosAccepted" className="text-sm text-theme-text-secondary">
                I have read and agree to the Terms of Service and Privacy Policy
                <span className="text-theme-danger"> *</span>
              </label>
            </div>
            {!canAcceptTos && (
              <p className="text-xs text-theme-text-tertiary">
                Please read both documents before agreeing
              </p>
            )}
          </div>
        )}

        <button
          type="submit"
          data-testid="auth-submit-button"
          disabled={loading}
          className="w-full py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill font-medium rounded transition py-3 rounded-xl shadow-lg shadow-theme-primary/20 text-base"
        >
          {loading
            ? 'Please wait...'
            : mode === 'login'
              ? 'Sign In'
              : mode === 'register'
                ? 'Create Account'
                : 'Send Reset Link'}
        </button>
      </form>

      {/* OAuth divider and buttons - show on login/register only when providers are configured */}
      {mode !== 'forgot' && (oauthGoogle || oauthApple) && (
        <div className="mt-5">
          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-theme-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-theme-surface px-3 text-theme-text-tertiary">
                Or continue with
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            {oauthGoogle && (
              <>
                <button
                  type="button"
                  onClick={onGoogleSignIn}
                  disabled={!!oauthLoading || loading}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 bg-theme-bg hover:bg-theme-border border border-theme-border rounded-xl text-sm font-medium text-theme-text transition-colors disabled:opacity-50 ${googleFallback ? 'hidden' : ''}`}
                >
                  {oauthLoading === 'google' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                  Google
                </button>
                <div
                  ref={googleBtnRef}
                  className={`flex-1 flex items-center justify-center overflow-hidden rounded-xl ${googleFallback ? '' : 'hidden'}`}
                />
              </>
            )}

            {oauthApple && (
              <button
                type="button"
                onClick={onAppleSignIn}
                disabled={!!oauthLoading || loading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-theme-bg hover:bg-theme-border border border-theme-border rounded-xl text-sm font-medium text-theme-text transition-colors disabled:opacity-50"
              >
                {oauthLoading === 'apple' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                )}
                Apple
              </button>
            )}
          </div>
        </div>
      )}

      {mode === 'login' && (
        <p className="mt-4 text-center text-sm text-theme-text-secondary">
          Forgot password?{' '}
          <button
            type="button"
            data-testid="auth-forgot-password-link"
            className="text-theme-primary hover:text-theme-primary-hover"
            onClick={() => onSwitchMode('forgot')}
          >
            Reset it
          </button>
        </p>
      )}

      {mode === 'forgot' && (
        <p className="mt-4 text-center text-sm text-theme-text-secondary">
          Remember your password?{' '}
          <button
            type="button"
            data-testid="auth-back-to-signin"
            className="text-theme-primary hover:text-theme-primary-hover"
            onClick={() => onSwitchMode('login')}
          >
            Sign in
          </button>
        </p>
      )}
    </div>
  );
}
